import { useState, useRef, useCallback } from 'react';
import { Icon, Spinner } from './ui/Icon.js';
import { Button } from './ui/Button.js';
import { apiFetch } from '../lib/api.js';
import type { KeywordsUploadResponse } from '../types/index.js';

export interface KeywordsUploadModalProps {
  tenantId: string;
  brandId: string;
  brandName: string;
  onClose: () => void;
  onUploaded: (result: KeywordsUploadResponse['data']) => void;
}

const MAX_BYTES = 10 * 1024 * 1024;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export function KeywordsUploadModal({
  tenantId,
  brandId,
  brandName,
  onClose,
  onUploaded,
}: KeywordsUploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [csvText, setCsvText] = useState<string>('');
  const [lineCount, setLineCount] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (f: File) => {
    setError(null);
    if (f.size > MAX_BYTES) {
      setError(`File is ${formatBytes(f.size)} — max is 10 MB`);
      setFile(null);
      setCsvText('');
      setLineCount(0);
      return;
    }
    const text = await f.text();
    const cleaned = text.replace(/^﻿/, '');
    // Rough line count for the preview (data rows = lines - header - blanks)
    const all = cleaned.split(/\r\n|\n|\r/).filter((l) => l.trim() !== '');
    setFile(f);
    setCsvText(text);
    setLineCount(Math.max(0, all.length - 1));
  }, []);

  const handleSubmit = async () => {
    if (!csvText) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/keywords/upload`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'text/csv' },
          body: csvText,
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Upload failed (${res.status})`);
      }
      const body = (await res.json()) as KeywordsUploadResponse;
      onUploaded(body.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="keywords-upload-modal"
    >
      <div className="w-full max-w-lg rounded-xl border border-[var(--hub-color-line)] bg-white p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Upload keywords</h2>
          <Button round onClick={onClose} aria-label="Close">
            <Icon name="bx-x" />
          </Button>
        </div>

        <p className="mt-2 text-sm text-slate-500">
          Upload a CSV of keywords for <span className="font-medium text-slate-700">{brandName}</span>.
          Required column: <code className="rounded-md bg-slate-100 px-1 font-mono text-xs">Keyword</code>.
          Optional: <code className="font-mono text-xs">Volume</code>,{' '}
          <code className="font-mono text-xs">Position</code>, <code className="font-mono text-xs">URL</code>,{' '}
          <code className="font-mono text-xs">Intent</code>. Ahrefs export shape is also supported.
        </p>

        <div className="mt-4">
          <label
            htmlFor="keywords-csv-file"
            className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center hover:border-violet-400 hover:bg-violet-50"
          >
            <Icon name="bx-upload" className="text-slate-400" />
            <span className="mt-2 text-sm font-medium text-slate-700">
              {file ? 'Choose a different file' : 'Click to choose a CSV'}
            </span>
            <span className="mt-1 text-xs text-slate-500">CSV / TSV up to 10 MB</span>
            <input
              ref={inputRef}
              id="keywords-csv-file"
              type="file"
              accept=".csv,.tsv,text/csv,text/tab-separated-values,text/plain"
              className="hidden"
              data-testid="keywords-upload-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
            />
          </label>
        </div>

        {file && (
          <div className="mt-3 flex items-center gap-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
            <Icon name="bx-file-blank" className="text-slate-400" />
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-slate-800" data-testid="keywords-upload-filename">
                {file.name}
              </div>
              <div className="text-xs text-slate-500">
                {formatBytes(file.size)} · ~{lineCount.toLocaleString()} data rows
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <div className="flex gap-2">
            <Icon name="bx-error" className="mt-0.5 shrink-0" />
            <span>
              Uploading replaces this brand's previously-uploaded keyword set. DataForSEO data is preserved
              except where it overlaps with an uploaded keyword (upload wins).
            </span>
          </div>
        </div>

        {error && (
          <p className="mt-3 text-sm text-red-600" data-testid="keywords-upload-error">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!csvText || submitting}
            data-testid="keywords-upload-submit"
          >
            {submitting ? <Spinner /> : <Icon name="bx-upload" />}
            {submitting ? 'Uploading…' : 'Upload'}
          </Button>
        </div>
      </div>
    </div>
  );
}
